import { useMutation } from '@tanstack/react-query';
import { formatISO, parseISO } from 'date-fns';
import { appointmentsApi } from '@/lib/api/appointments';
import { stanNotify } from '@/lib/stanNotify';

export interface BlockTimeParams {
    id?: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    silent?: boolean; // Don't show notification if true
}

export function useBlockTime() {
    const mutation = useMutation({
        mutationFn: async (blockTime: BlockTimeParams) => {
            // Convert local timezone to UTC
            const startDateTime = parseISO(`${blockTime.startDate}T${blockTime.startTime}`);
            const endDateTime = parseISO(`${blockTime.endDate}T${blockTime.endTime}`);

            return await appointmentsApi.blockTime({
                start: formatISO(startDateTime),
                end: formatISO(endDateTime),
                ...(blockTime.id && { id: Number(blockTime.id) }),
            });
        },
        onSuccess: (_data, variables) => {
            // Skip notification if silent flag is set
            if (variables.silent) return;

            const isEdit = !!variables.id;
            stanNotify({
                type: 'success',
                title: isEdit ? 'Time Block Updated' : 'Time Blocked Successfully',
                text: isEdit ? 'The time block has been updated!' : 'The selected time has been blocked!',
            });
        },
        onError: (error: Error) => {
            console.error('Block Time Error:', error);
            stanNotify({
                type: 'error',
                title: 'Failed to Block Time',
                text: `There was an error blocking the time: ${error.message || 'Unknown error'}`,
            });
        },
    });

    return {
        blockTime: mutation.mutateAsync,
        isBlocking: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
    };
}

export function useDeleteBlockedTime() {
    const mutation = useMutation({
        mutationFn: async ({ blockedTimeId }: { blockedTimeId: string }) => {
            return await appointmentsApi.deleteBlockedTime(blockedTimeId);
        },
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Time Block Removed',
                text: 'The time block has been removed successfully!',
            });
        },
        onError: (error: Error) => {
            console.error('Delete Blocked Time Error:', error);
            stanNotify({
                type: 'error',
                title: 'Failed to Remove Time Block',
                text: `There was an error removing the time block: ${error.message || 'Unknown error'}`,
            });
        },
    });

    return {
        deleteBlockedTime: mutation.mutateAsync,
        isDeleting: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
    };
}
